/**
 * QA Console — ตัวช่วยจัดรูปแบบการแสดงผล
 * แยกจาก qaScoring.js เพราะอันนั้นเป็นกฎธุรกิจ อันนี้เป็นเรื่องหน้าจอล้วน ๆ
 */

const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

/** "03 ส.ค." — ใช้ในตารางที่พื้นที่จำกัด */
export function formatDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')} ${TH_MONTHS_SHORT[d.getMonth()]}`;
}

/** "03 ส.ค. 2026 · 09:12" */
export function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${String(d.getDate()).padStart(2, '0')} ${TH_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543} · ${time}`;
}

/** วินาที → "5:18" */
export function formatDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ป้ายผลการตัดสิน + สถานะการประเมิน (Q9)
 *
 * ⚠️ pending/not-evaluated/skipped/failed เป็น **สถานะของงานประเมิน** ไม่ใช่ผลตัดสิน —
 *    อยู่ map เดียวกันเพราะขึ้นบน chip ตัวเดียวกันในตาราง/หัวเคส
 * ⚠️ 'failed' คือ "งานประเมินล้ม" (LLM ตอบเพี้ยน/timeout) ห้ามสับสนกับ 'fail'
 *    ที่แปลว่าเจ้าหน้าที่สอบตก — สองคำนี้คนละชะตากรรมกันมาก
 */
export function statusLabel(status) {
  return {
    pass: 'ผ่าน',
    borderline: 'เฉียดเกณฑ์',
    fail: 'ไม่ผ่าน',
    pending: 'รอประเมิน',
    // เคสยังเปิด — คนละคำกับ "รอประเมิน" โดยตั้งใจ ปุ่มกรองไม่มีค่านี้ (ดู QaVerdictStatus.InProgress)
    'in-progress': 'ยังไม่จบสนทนา',
    'not-evaluated': 'ไม่ได้ประเมิน',
    skipped: 'ข้ามการประเมิน',
    failed: 'ประเมินไม่สำเร็จ',
  }[status] || status;
}

/** สี Vuetify ของผลการตัดสิน / สถานะการประเมิน */
export function statusColor(status) {
  return {
    pass: 'success',
    borderline: 'warning',
    fail: 'error',
    // ⚠️ skipped/not-evaluated เป็นสีเทา ไม่ใช่ส้มแดง — เป็นเรื่องปกติที่ตั้งใจ
    //    (เคสสั้น/นอกกลุ่มสุ่ม) ไม่ใช่สิ่งผิดปกติที่ต้องรีบไปดู
    pending: 'info',
    'in-progress': 'grey',
    'not-evaluated': 'grey',
    skipped: 'grey',
    // ⚠️ failed แดงจริง — งานประเมินล้มต้องมีคนเห็นแล้วกดประเมินใหม่
    failed: 'error',
  }[status] || 'grey';
}

/** ป้ายความสำคัญของเคสสอบสวน */
export function priorityLabel(priority) {
  return { High: 'สูง', Medium: 'กลาง', Low: 'ต่ำ', Review: 'รอตรวจ' }[priority] || priority;
}

export function priorityColor(priority) {
  return { High: 'error', Medium: 'warning', Low: 'grey', Review: 'info' }[priority] || 'grey';
}

/** แปลงคะแนนอารมณ์ 0–100 เป็นขั้ว −1.00 ถึง +1.00 */
export function toPolarity(score) {
  const p = Math.max(-1, Math.min(1, (score - 50) / 50));
  return `${p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(2)}`;
}

/** คำอธิบายอารมณ์ 2 ภาษา + ขั้ว (ใช้ระบายสี) */
export function emotionOf(score, who) {
  if (who === 'agent') {
    if (score < 38) return { th: 'ลนลาน', en: 'Flustered', valence: 'neg' };
    if (score < 50) return { th: 'ตึงเครียด', en: 'Tense', valence: 'neg' };
    if (score < 65) return { th: 'ใจเย็น', en: 'Composed', valence: 'pos' };
    if (score < 78) return { th: 'เป็นมิตร', en: 'Warm', valence: 'pos' };
    return { th: 'ร่าเริง', en: 'Upbeat', valence: 'pos' };
  }
  if (score < 25) return { th: 'โกรธ', en: 'Angry', valence: 'neg' };
  if (score < 40) return { th: 'หงุดหงิด', en: 'Frustrated', valence: 'neg' };
  if (score < 50) return { th: 'กังวล', en: 'Uneasy', valence: 'neg' };
  if (score < 62) return { th: 'เฉย ๆ', en: 'Neutral', valence: 'neutral' };
  if (score < 76) return { th: 'พอใจ', en: 'Satisfied', valence: 'pos' };
  return { th: 'มีความสุข', en: 'Happy', valence: 'pos' };
}

/** สรุปทิศทางการเปลี่ยนของอารมณ์ */
export function sentimentShift(delta) {
  if (delta >= 20) return { icon: 'mdi-trending-up', label: 'พลิกกลับมาดี', color: 'success' };
  if (delta >= 8) return { icon: 'mdi-trending-up', label: 'ดีขึ้น', color: 'success' };
  if (delta > -8) return { icon: 'mdi-trending-neutral', label: 'คงที่', color: 'grey' };
  if (delta > -20) return { icon: 'mdi-trending-down', label: 'แย่ลง', color: 'error' };
  return { icon: 'mdi-trending-down', label: 'แย่ลงมาก', color: 'error' };
}
